// frontend/src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// Import Page Components (ensure all are correctly imported)
import HomePage from '../views/HomePage.vue';
import UnitsPage from '../views/UnitsPage.vue';
import TenantsPage from '../views/TenantsPage.vue';
import OccupancyPage from '../views/OccupancyPage.vue';
import PaymentsPage from '../views/PaymentsPage.vue';
import AboutPage from '../views/AboutPage.vue';
import LoginPage from '../views/LoginPage.vue';
import RegisterPage from '../views/RegisterPage.vue';
import UserRolesPage from '../views/UserRolesPage.vue';
import MaintenanceRequestsPage from '../views/MaintenanceRequestsPage.vue';
import ExpensesPage from '../views/ExpensesPage.vue';
import VendorsPage from '../views/VendorsPage.vue';
import GoogleAuthSuccess from '../views/GoogleAuthSuccess.vue';
import GoogleAuthError from '../views/GoogleAuthError.vue';
import HelpPage from '../views/HelpPage.vue';
import ForgotPassword from '../views/ForgotPassword.vue';
import ResetPassword from '../views/ResetPassword.vue';
import ChangePassword from '../views/ChangePassword.vue';
import ReceiptPrint from '../components/ReceiptPrint.vue';
import CompaniesPage from '../views/CompaniesPage.vue';
import CurrenciesPage from '../views/CurrenciesPage.vue';
import EmployeesPage from '../views/EmployeesPage.vue';
import CommissionsWagesPage from '../views/CommissionsWagesPage.vue';
import FinancialReportsPage from '../views/FinancialReportsPage.vue';
import CountriesPage from '../views/CountriesPage.vue';

const routes = [
  { path: '/', name: 'Home', component: HomePage, meta: { requiresAuth: true } },
  { path: '/login', name: 'Login', component: LoginPage, meta: { guestOnly: true } },
  { path: '/register', name: 'Register', component: RegisterPage, meta: { guestOnly: true } },
  { path: '/units', name: 'Units', component: UnitsPage, meta: { requiresAuth: true } },
  { path: '/tenants', name: 'Tenants', component: TenantsPage, meta: { requiresAuth: true } },
  { path: '/occupancy', name: 'Occupancy', component: OccupancyPage, meta: { requiresAuth: true } },
  { path: '/payments', name: 'Payments', component: PaymentsPage, meta: { requiresAuth: true } },
  { path: '/admin/user-roles', name: 'UserRoles', component: UserRolesPage, meta: { requiresAuth: true, roles: ['admin'] } },
  { path: '/about', name: 'About', component: AboutPage },
  { path: '/help', name: 'Help', component: HelpPage },
  { path: '/maintenance-requests', name: 'MaintenanceRequests', component: MaintenanceRequestsPage, meta: { requiresAuth: true } },
  { path: '/expenses',            name: 'Expenses',            component: ExpensesPage,            meta: { requiresAuth: true } },
  { path: '/vendors',             name: 'Vendors',             component: VendorsPage,             meta: { requiresAuth: true } },
  { path: '/employees',           name: 'Employees',           component: EmployeesPage,           meta: { requiresAuth: true } },
  { path: '/commission-wages',    name: 'CommissionWages',    component: CommissionsWagesPage,    meta: { requiresAuth: true } },
  { path: '/auth/google/success', name: 'GoogleAuthSuccess',  component: GoogleAuthSuccess },
  { path: '/auth/google/error', name: 'GoogleAuthError', component: GoogleAuthError },
  // { path: '/auth/google/callback', name: 'GoogleAuthCallback', component: GoogleAuthCallback },

  { path: '/forgot', name: 'ForgotPassword', component: ForgotPassword },
  { path: '/reset', name: 'ResetPassword', component: ResetPassword },
  { path: '/change-password', name: 'ChangePassword', component: ChangePassword },
  { path: '/companies', name: 'Companies', component: CompaniesPage, meta: { requiresAuth: true } },
  { path: '/countries', name: 'Countries', component: CountriesPage, meta: { requiresAuth: true } },
  { path: '/currencies', name: 'Currencies', component: CurrenciesPage, meta: { requiresAuth: true } },
  { path: '/payments/:id/receipt', name: 'ReceiptPrint', component: ReceiptPrint, meta: { requiresAuth: true } },
  { path: '/financial-reports', name: 'FinancialReports', component: FinancialReportsPage, meta: { requiresAuth: true } },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

let initialAuthCheckDone = false;

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check if user is trying to access protected routes after logout
  const hasLoggedOut = sessionStorage.getItem('user_logged_out');
  
  // If user has logged out and is trying to access protected routes, redirect to login
  if (hasLoggedOut && to.meta.requiresAuth) {
    sessionStorage.removeItem('user_logged_out');
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  // Perform initial user status check only once per app load, or if not logged in
  if (!initialAuthCheckDone && !authStore.isLoggedIn) {
    await authStore.fetchUserStatus();
    initialAuthCheckDone = true; 
  } else if (!authStore.isLoggedIn && to.meta.requiresAuth) {
    // If subsequent navigation to protected route and not logged in, re-check status
    // This handles cases where user might have been logged out in another tab
    // but Pinia state isn't updated yet.
    await authStore.fetchUserStatus();
  }

  const isAuthenticated = authStore.isAuthenticated;

  // Additional check: if localStorage has session but auth store doesn't, clear localStorage
  if (!isAuthenticated && typeof window !== 'undefined') {
    const session = localStorage.getItem('user_session');
    if (session) {
      console.warn('Found localStorage session but auth store is not authenticated. Clearing localStorage.');
      localStorage.removeItem('user_session');
    }
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
  } else if (to.meta.guestOnly && isAuthenticated) {
    next({ name: 'Home' });
  } else if (to.meta.roles) {
    if (!isAuthenticated) {
      next({ name: 'Login', query: { redirect: to.fullPath } });
    } else {
      const userRoles = authStore.user?.roles || [];
      const hasRequiredRole = to.meta.roles.some(role => userRoles.includes(role));
      if (hasRequiredRole) {
        next();
      } else {
        console.warn(`User does not have required roles (${to.meta.roles}) for route: ${to.path}. Redirecting home.`);
        next({ name: 'Home' }); // Or a dedicated 'Access Denied' page
      }
    }
  } else {
    next();
  }
});

export default router;
